import theme from '@/common/theme';
import Alert from '@/components/alert/alert-component';
import FileUpload from '@/components/file-upload/file-upload-component';
import { Col, Row } from '@/components/grid/grid-component';
import Progress from '@/components/progress-bar/progress-bar-component';
import { S3Client } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiDownload } from 'react-icons/fi';
import { createTemplateExcelWithData } from '@/utils/excel-utils';
import getCompanyTempPriceListApi from '@/api/get/company/company-pricing/get-company-temp-price-list';
import styled from 'styled-components';
import PermissionWrapper from '../permission-wrapper/permission-wrapper-component';
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from '../button/button-component';
import Styled from './pricing-all-style';

const modalRowStyle = {
  marginLeft: '0px',
  marginRight: '0px',
  marginBottom: '0px',
};

const modalColStyle = {
  paddingLeft: '0px',
  paddingRight: '0px',
};

const downloadTemplateStyledButton = {
  border: 'unset',
  backgroundColor: 'unset',
};

const Body = styled.div`
  width: 100%;
  display: block;
  position: relative;
  margin-bottom: 20px;
  padding: 16px;
`;

/**
 * Migrate a pricing modal
 * @param {object} root0 - The root object containing props
 * @param {string} root0.companyId - CompanyId that is either from a selected Supplier or Company itself
 * @param {Function} root0.onSubmit - Callback function to submit the form
 * @returns {JSX.Element} - The rendered JSX element
 */
const MigratePricingModal = ({ companyId, onSubmit }) => {
  const [progress, setProgress] = useState(0);
  const [filesAccepted, setFilesAccepted] = useState([]);
  const [downloadTemplate, setDownloadTemplate] = useState(false);
  const { setValue, handleSubmit } = useFormContext();
  const uploadToS3 = async (fileToUpload) => {
    const file = fileToUpload[0];
    const target = {
      Bucket: import.meta.env.VITE_BUCKET_NAME,
      Key: `pricing-upload/${file.name}`,
      Body: file,
    };

    let parts = {};

    if (file.size >= 1024 * 1024 * 2) {
      parts = {
        queueSize: 4, // optional concurrency configuration
        partSize: 1024 * 1024 * 2, // optional size of each part, in bytes, at least 5MB
      };
    }

    try {
      const parallelUploads3 = new Upload({
        client: new S3Client({
          region: import.meta.env.VITE_REGION ?? 'eu-west-1',
          credentials: {
            accessKeyId: import.meta.env.VITE_BUCKET_ACCESS_KEY,
            secretAccessKey: import.meta.env.VITE_BUCKET_SECRET_KEY,
          },
        }),
        params: target,

        tags: [
          /* ... */
        ], // optional tags
        ...parts,
        leavePartsOnError: false, // optional manually handle dropped parts
      });

      parallelUploads3.on('httpUploadProgress', (prog) => {
        if (prog.total === prog.loaded) {
          setProgress(100);
        } else {
          setProgress(Math.round((prog.loaded / prog.total) * 100));
        }
      });

      await parallelUploads3.done().then(() => {
        setValue('file_name', file.name);
        handleSubmit(onSubmit)();
      });
    } catch (e) {
      Alert({
        icon: 'error',
        text: e.message,
      });
    }
  };

  const [fileName, setFileName] = useState('');
  const [isDropped, setIsDropped] = useState(false);

  const onDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      Alert({
        icon: 'error',
        text: 'Please upload only XLSX files.',
      });
      setIsDropped(false);
      setFileName('');
    } else {
      acceptedFiles.map((file) => setFileName(file.name));
      setFilesAccepted(acceptedFiles);

      setIsDropped(true);
    }
  };

  return (
    <Row style={modalRowStyle}>
      <Body>
        {progress === 0 || progress >= 100 ? (
          <Col style={modalColStyle} xs={12}>
            <FileUpload
              fileType="xlsx"
              onDrop={onDrop}
              fileName={fileName}
              isDropped={isDropped}
            />
          </Col>
        ) : (
          <Col style={modalColStyle} xs={12}>
            <Progress
              fileName={fileName}
              progress={progress}
              color={theme.COLOR_PRIMARY}
            />
          </Col>
        )}
      </Body>
      <Styled.RowStyle>
        <PrimaryOutlineButton
          buttonWidth="fit-content"
          icon={<FiDownload />}
          disabled={false}
          margin="0 0 0 0"
          type="button"
          loading={downloadTemplate}
          style={downloadTemplateStyledButton}
          title="Download Template"
          onClick={() => {
            setDownloadTemplate(true);

            getCompanyTempPriceListApi(companyId).then((res) => {
              const tempPartNumbers = res.data.map((item) => ({
                temp_part_number: item.part_number,
                part_number: '',
              }));
              createTemplateExcelWithData('pricing-template', tempPartNumbers);
              setDownloadTemplate(false);
            });
          }}
        />
        <PermissionWrapper
          permissions={['price.create', 'price.read', 'price.update']}
          satisfyAll={false}
        >
          <PrimaryButton
            buttonWidth="108px"
            disabled={false}
            margin="0 0 0 0"
            type="button"
            loading={downloadTemplate}
            title="Migrate"
            onClick={() => {
              uploadToS3(filesAccepted);
            }}
          />
        </PermissionWrapper>
      </Styled.RowStyle>
    </Row>
  );
};

MigratePricingModal.propTypes = {
  companyId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default MigratePricingModal;
